import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Building2, Search, Users } from 'lucide-react';

export default function Kunden() {
  const { API } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');
  const [form, setForm] = useState({ name: '', company: '', email: '', phone: '', address: '', city: '', zip_code: '', tax_id: '', customer_type: 'gewerbe', notes: '' });

  useEffect(() => {
    axios.get(`${API}/customers`).then(r => setCustomers(r.data)).catch(() => {});
  }, [API]);

  const handleSave = async () => {
    try {
      if (editing) {
        const res = await axios.put(`${API}/customers/${editing}`, form);
        setCustomers(prev => prev.map(c => c.id === editing ? res.data : c));
        toast.success('Kunde aktualisiert');
      } else {
        const res = await axios.post(`${API}/customers`, form);
        setCustomers(prev => [...prev, res.data]);
        toast.success('Kunde erstellt');
      }
      setShowForm(false);
      setEditing(null);
      setForm({ name: '', company: '', email: '', phone: '', address: '', city: '', zip_code: '', tax_id: '', notes: '' });
    } catch { toast.error('Fehler'); }
  };

  const startEdit = (c) => {
    setEditing(c.id);
    setForm({ name: c.name, company: c.company, email: c.email, phone: c.phone, address: c.address, city: c.city, zip_code: c.zip_code, tax_id: c.tax_id || '', customer_type: c.customer_type || 'gewerbe', notes: c.notes });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    await axios.delete(`${API}/customers/${id}`);
    setCustomers(prev => prev.filter(c => c.id !== id));
    toast.success('Gelöscht');
  };

  const filtered = customers.filter(c =>
    c.name?.toLowerCase().includes(search.toLowerCase()) ||
    c.company?.toLowerCase().includes(search.toLowerCase()) ||
    c.email?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-4 animate-fade-in" data-testid="kunden-page">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-white tracking-tight">Kunden</h1>
        <Button onClick={() => { setEditing(null); setForm({ name: '', company: '', email: '', phone: '', address: '', city: '', zip_code: '', tax_id: '', customer_type: 'gewerbe', notes: '' }); setShowForm(true); }}
          data-testid="create-customer-button"
          className="bg-amber-500 text-slate-950 hover:bg-amber-400 font-bold rounded-sm shadow-[0_0_15px_rgba(245,158,11,0.3)]">
          <Plus className="w-4 h-4 mr-2" />Neuer Kunde
        </Button>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
        <Input data-testid="customer-search" value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Kunden suchen..." className="pl-10 bg-slate-950 border-slate-800 text-slate-200 placeholder:text-slate-600" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map(c => (
          <div key={c.id} className="bg-slate-900/50 border border-slate-800/60 backdrop-blur-md rounded-sm p-5 group" data-testid={`customer-card-${c.id}`}>
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-sm bg-amber-500/10 border border-amber-500/20 flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-amber-400" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-sm font-semibold text-white">{c.name}</p>
                  {c.company && <p className="text-xs text-slate-400">{c.company}</p>}
                </div>
              </div>
              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <Button variant="ghost" size="icon" onClick={() => startEdit(c)} className="w-7 h-7 text-slate-400 hover:text-amber-400">
                  <Pencil className="w-3.5 h-3.5" />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => handleDelete(c.id)} className="w-7 h-7 text-slate-400 hover:text-rose-400">
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
            <div className="space-y-1 text-xs text-slate-400">
              {c.email && <p>{c.email}</p>}
              {c.phone && <p>{c.phone}</p>}
              {c.address && <p>{c.address}, {c.zip_code} {c.city}</p>}
              {c.tax_id && <p className="font-mono text-slate-500">USt-IdNr.: {c.tax_id}</p>}
              <p className={`text-[10px] font-medium mt-1 ${c.customer_type === 'privat' ? 'text-cyan-400' : 'text-amber-400'}`}>
                {c.customer_type === 'privat' ? 'Privatkunde' : 'Gewerbekunde'}
              </p>
            </div>
          </div>
        ))}
      </div>
      {!filtered.length && <div className="text-center text-slate-500 py-8">Keine Kunden gefunden</div>}

      <Dialog open={showForm} onOpenChange={v => { setShowForm(v); if (!v) setEditing(null); }}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-200">
          <DialogHeader><DialogTitle className="text-white">{editing ? 'Kunde bearbeiten' : 'Neuer Kunde'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="flex gap-3 mb-1">
              <label className={`flex-1 flex items-center gap-2 px-4 py-3 rounded-sm border cursor-pointer transition-colors ${form.customer_type === 'gewerbe' ? 'border-amber-500 bg-amber-500/10 text-amber-400' : 'border-slate-800 text-slate-400 hover:border-slate-700'}`}
                onClick={() => setForm({...form, customer_type: 'gewerbe'})}>
                <Building2 className="w-4 h-4" /><span className="text-sm font-medium">Gewerbekunde</span>
              </label>
              <label className={`flex-1 flex items-center gap-2 px-4 py-3 rounded-sm border cursor-pointer transition-colors ${form.customer_type === 'privat' ? 'border-amber-500 bg-amber-500/10 text-amber-400' : 'border-slate-800 text-slate-400 hover:border-slate-700'}`}
                onClick={() => setForm({...form, customer_type: 'privat'})}>
                <Users className="w-4 h-4" /><span className="text-sm font-medium">Privatkunde</span>
              </label>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-slate-300">Name</Label>
                <Input data-testid="customer-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                  placeholder="Vorname Nachname" className="bg-slate-950 border-slate-800 text-slate-200 placeholder:text-slate-600 mt-1" />
              </div>
              <div>
                <Label className="text-slate-300">Firma</Label>
                <Input data-testid="customer-company" value={form.company} onChange={e => setForm({ ...form, company: e.target.value })}
                  placeholder="Firma GmbH" className="bg-slate-950 border-slate-800 text-slate-200 placeholder:text-slate-600 mt-1" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-slate-300">E-Mail</Label>
                <Input data-testid="customer-email" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                  className="bg-slate-950 border-slate-800 text-slate-200 mt-1" />
              </div>
              <div>
                <Label className="text-slate-300">Telefon</Label>
                <Input data-testid="customer-phone" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })}
                  className="bg-slate-950 border-slate-800 text-slate-200 mt-1" />
              </div>
            </div>
            <div>
              <Label className="text-slate-300">Adresse</Label>
              <Input data-testid="customer-address" value={form.address} onChange={e => setForm({ ...form, address: e.target.value })}
                className="bg-slate-950 border-slate-800 text-slate-200 mt-1" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-slate-300">PLZ</Label>
                <Input data-testid="customer-zip" value={form.zip_code} onChange={e => setForm({ ...form, zip_code: e.target.value })}
                  className="bg-slate-950 border-slate-800 text-slate-200 mt-1" />
              </div>
              <div>
                <Label className="text-slate-300">Stadt</Label>
                <Input data-testid="customer-city" value={form.city} onChange={e => setForm({ ...form, city: e.target.value })}
                  className="bg-slate-950 border-slate-800 text-slate-200 mt-1" />
              </div>
            </div>
            <div>
              <Label className="text-slate-300">USt-IdNr. / Steuernummer</Label>
              <Input data-testid="customer-tax-id" value={form.tax_id} onChange={e => setForm({ ...form, tax_id: e.target.value })}
                placeholder="z.B. DE123456789" className="bg-slate-950 border-slate-800 text-slate-200 placeholder:text-slate-600 mt-1" />
            </div>
            <div>
              <Label className="text-slate-300">Notizen</Label>
              <Textarea data-testid="customer-notes" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
                className="bg-slate-950 border-slate-800 text-slate-200 mt-1" />
            </div>
            <Button onClick={handleSave} data-testid="save-customer" className="w-full bg-amber-500 text-slate-950 hover:bg-amber-400 font-bold rounded-sm">
              {editing ? 'Speichern' : 'Erstellen'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
